import { Box, Button, InputAdornment, Link, MenuItem, Rating, Select, Stack, TextField, Typography } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import { useQuery } from '@tanstack/react-query';
import moment from 'moment';
import { useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { Link as RouterLink } from 'react-router-dom';
import DataTable from '../components/DataTable';
import PageHeader from '../components/PageHeader';
import Pagination from '../components/pagination';
import { api } from '../shared/service/dataprovider';
import tokens from '../shared/theme/tokens';

const ALL_PROPERTIES = '__all__';
const blank = value => value || '—';
const formatDate = value => (value ? moment(value).format('MM/DD/YYYY') : '—');
const formatDateTime = value => (value ? moment(value).format('MMM D, YYYY h:mm A') : '—');
const EMPTY_ROWS = [];

async function downloadSurveyCsv({ propertyId, from, to }) {
  const params = new URLSearchParams();
  if (propertyId) params.set('propertyId', propertyId);
  if (from) params.set('from', from);
  if (to) params.set('to', to);
  const response = await api.get(`/v1/admin/move-in-condition/surveys/export?${params.toString()}`, {
    responseType: 'blob',
  });
  const blob = new Blob([response.data], { type: 'text/csv;charset=utf-8' });
  const url = window.URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = `move-in-satisfaction-${moment().format('YYYY-MM-DD')}.csv`;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  window.URL.revokeObjectURL(url);
}

export default function MoveInSurveys() {
  // Local filter (not the shared Leases/Applicants selection) so "All properties"
  // can omit propertyId without wiping other pages' property scope.
  const [propertyFilter, setPropertyFilter] = useState(ALL_PROPERTIES);
  const [unit, setUnit] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [currentPage, setCurrentPage] = useState(1);
  const [exporting, setExporting] = useState(false);

  const propertyId = propertyFilter === ALL_PROPERTIES ? undefined : propertyFilter;

  const { data: properties } = useQuery({
    queryKey: ['properties'],
    queryFn: () => api.get('/v1/admin/properties'),
  });

  useEffect(() => {
    setCurrentPage(1);
  }, [propertyFilter, unit, from, to]);

  const {
    data: surveys,
    isLoading,
    isFetching,
  } = useQuery({
    queryKey: ['moveInSurveys', propertyFilter, from, to],
    queryFn: () =>
      api.get('/v1/admin/move-in-condition/surveys', {
        params: {
          propertyId,
          from: from || undefined,
          to: to || undefined,
        },
      }),
  });

  const rawRows = surveys?.data?.rows ?? EMPTY_ROWS;

  const rows = useMemo(() => {
    const needle = unit.trim().toLowerCase();
    if (!needle) return rawRows;
    return rawRows.filter(row =>
      String(row.unit || '')
        .toLowerCase()
        .includes(needle)
    );
  }, [rawRows, unit]);

  const visibleRows = useMemo(() => rows.slice(rowsPerPage * (currentPage - 1), rowsPerPage * currentPage), [rows, rowsPerPage, currentPage]);

  const onExportCsv = async () => {
    try {
      setExporting(true);
      await downloadSurveyCsv({ propertyId, from, to });
      toast.success('Survey CSV downloaded.');
    } catch (err) {
      toast.error(err?.response?.data?.message ?? 'Could not export survey CSV.');
    } finally {
      setExporting(false);
    }
  };

  const columns = [
    {
      key: 'property_name',
      header: 'Property',
      render: row => blank(row.property_name),
      sx: { minWidth: 160 },
    },
    {
      key: 'unit',
      header: 'Unit',
      render: row =>
        row.lease_id ? (
          <Link component={RouterLink} to={`/lease/${row.lease_id}`} underline="hover">
            {blank(row.unit)}
          </Link>
        ) : (
          blank(row.unit)
        ),
      sx: { minWidth: 100 },
    },
    {
      key: 'submitted_at',
      header: 'Submitted',
      render: row => formatDateTime(row.submitted_at),
      sx: { minWidth: 180 },
    },
    {
      key: 'move_in_date',
      header: 'Move-In Date',
      render: row => formatDate(row.move_in_date),
      sx: { minWidth: 130 },
    },
    {
      key: 'average_score',
      header: 'Average',
      render: row => (
        <Stack direction="row" alignItems="center" gap={1}>
          <Rating value={Number(row.average_score) || 0} precision={0.1} readOnly size="small" />
          <Typography variant="body2">{row.average_score || '—'}</Typography>
        </Stack>
      ),
      sx: { minWidth: 180 },
    },
    { key: 'property_team', header: 'Property Team', render: row => row.property_team ?? '—', sx: { minWidth: 120 } },
    { key: 'apartment_satisfaction', header: 'Apartment', render: row => row.apartment_satisfaction ?? '—', sx: { minWidth: 110 } },
    { key: 'property_cleanliness', header: 'Cleanliness', render: row => row.property_cleanliness ?? '—', sx: { minWidth: 110 } },
    { key: 'move_in_process', header: 'Move-In Process', render: row => row.move_in_process ?? '—', sx: { minWidth: 140 } },
    { key: 'property_satisfaction', header: 'Satisfaction', render: row => row.property_satisfaction ?? '—', sx: { minWidth: 110 } },
    { key: 'recommend_likelihood', header: 'NPS (1-10)', render: row => row.recommend_likelihood || '—', sx: { minWidth: 110 } },
    {
      key: 'community_amenities_satisfaction',
      header: 'Amenities (1-10)',
      render: row => row.community_amenities_satisfaction || '—',
      sx: { minWidth: 130 },
    },
    { key: 'keys_ready_on_time', header: 'Keys Ready', render: row => blank(row.keys_ready_on_time), sx: { minWidth: 110 } },
    { key: 'mailbox_key_received', header: 'Mailbox Key', render: row => blank(row.mailbox_key_received), sx: { minWidth: 120 } },
    {
      key: 'comments',
      header: 'Comments',
      render: row => (
        <Typography variant="body2" sx={{ maxWidth: 280, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {blank(row.comments)}
        </Typography>
      ),
      sx: { minWidth: 220 },
    },
  ];

  return (
    <Box>
      <PageHeader
        title="Move-In Surveys"
        action={
          <Stack direction="row" gap={1.5} alignItems="center" flexWrap="wrap">
            <Select
              size="small"
              onChange={e => setPropertyFilter(e.target.value)}
              value={propertyFilter}
              sx={{ minWidth: 260, backgroundColor: 'background.paper' }}
            >
              <MenuItem value={ALL_PROPERTIES}>All properties</MenuItem>
              {properties?.data?.map(p => (
                <MenuItem key={p.id} value={p.id}>
                  {p.name}
                </MenuItem>
              ))}
            </Select>
            <Button variant="contained" size="small" startIcon={<FileDownloadOutlinedIcon />} onClick={onExportCsv} disabled={exporting}>
              {exporting ? 'Exporting…' : 'Export CSV'}
            </Button>
          </Stack>
        }
      />

      <Box sx={{ display: 'flex', gap: 1.5, mb: 3, flexWrap: 'wrap' }}>
        <TextField
          size="small"
          value={unit}
          onChange={e => setUnit(e.target.value)}
          placeholder="Unit"
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ fontSize: 20, color: 'text.secondary' }} />
              </InputAdornment>
            ),
          }}
          sx={{ minWidth: 180, backgroundColor: 'background.paper', borderRadius: `${tokens.radius.sm}px` }}
        />
        <TextField
          size="small"
          type="date"
          label="From"
          value={from}
          onChange={e => setFrom(e.target.value)}
          InputLabelProps={{ shrink: true }}
          sx={{ minWidth: 170, backgroundColor: 'background.paper', borderRadius: `${tokens.radius.sm}px` }}
        />
        <TextField
          size="small"
          type="date"
          label="To"
          value={to}
          onChange={e => setTo(e.target.value)}
          InputLabelProps={{ shrink: true }}
          sx={{ minWidth: 170, backgroundColor: 'background.paper', borderRadius: `${tokens.radius.sm}px` }}
        />
      </Box>

      <DataTable
        columns={columns}
        rows={visibleRows}
        rowKey={row => row.survey_id}
        loading={isLoading || isFetching}
        emptyContent="No move-in surveys found"
        minWidth={1560}
        footer={
          rows.length > 0 ? (
            <Box sx={{ px: 2, py: 1 }}>
              <Pagination
                currentPage={currentPage}
                totalPage={Math.ceil(rows.length / rowsPerPage)}
                rowsPerPage={rowsPerPage}
                onChangeRowsPerPage={p => {
                  setCurrentPage(1);
                  setRowsPerPage(p);
                }}
                onChangeCurrentPage={p => setCurrentPage(p)}
              />
            </Box>
          ) : null
        }
      />
    </Box>
  );
}
